import { ColumnConfig, Grid, GridConfig, SortDirection } from "fmko-ts-grid";
import { CertTestCaseRapportVO, CertTestCaseVO, CertTestVO, TestCaseStatus, TestSet, TestSetUtil, WhitelistingVO } from "./RestTypes";
import CertTestAdminApp from "./CertTestAdminApp";
import CertTestService from "./CertTestService";
import SystemInfoService from "./SystemInfoService";
import TestCaseRenderer from "./TestCaseRenderer";
import SubjectTestCaseWidget from "./SubjectTestCaseWidget";
import TestCaseResultRenderer from "./TestCaseResultRenderer";
import StartStopTestCaseWidget from "./StartStopTestCaseWidget";
import IgnoreTestCaseWidget from "./IgnoreTestCaseWidget";
import TestGroupRenderer from "./TestGroupRenderer";
import UseTestGroupRenderer from "./UseTestGroupRenderer";
import MultiLineRenderer from "./MultiLineRenderer";

export default class CertTestPanel {
    element: HTMLElement;
    grids: Array<Grid<CertTestCaseVO>>;
    groupGrid: Grid<string>;
    visible: boolean;
    currentTest: CertTestVO;
    systemOwnerSelect: HTMLSelectElement;
    systemNameSelect: HTMLSelectElement;
    systemVersionSelect: HTMLSelectElement;
    whitelistings: Array<WhitelistingVO>;
    selectedWhitelisting: WhitelistingVO;
    selectedGroups: Array<string>;
    testCasesTableDiv: HTMLDivElement;
    whitelistingDiv: HTMLDivElement;
    testGroupsTableDiv: HTMLDivElement;
    startCreateButton: HTMLButtonElement;
    createButton: HTMLButtonElement;
    removeButton: HTMLButtonElement;
    cloneButton: HTMLButtonElement;
    fetchButton: HTMLButtonElement;
    homeButton: HTMLButtonElement;
    rapportButton: HTMLButtonElement;
    testSetFormGroupDiv: HTMLDivElement;
    testSetDiv: HTMLDivElement;

    constructor() {

        let testNameInput = <HTMLInputElement>document.getElementById("field-test-name");
        this.fetchButton = <HTMLButtonElement>document.getElementById("button-fetch");
        this.startCreateButton = <HTMLButtonElement>document.getElementById("button-create-start");
        this.createButton = <HTMLButtonElement>document.getElementById("button-create");
        this.removeButton = <HTMLButtonElement>document.getElementById("button-remove");
        this.homeButton = <HTMLButtonElement>document.getElementById("button-home");
        this.cloneButton = <HTMLButtonElement>document.getElementById("button-clone");
        this.rapportButton = <HTMLButtonElement>document.getElementById("button-rapport");
        this.testCasesTableDiv = <HTMLDivElement>document.getElementById("table-test-cases");
        this.testGroupsTableDiv = <HTMLDivElement>document.getElementById("table-test-groups");
        this.selectedGroups = [];

        this.systemOwnerSelect = <HTMLSelectElement>document.getElementById("select-test-systemowner");
        this.systemNameSelect = <HTMLSelectElement>document.getElementById("select-test-systemname");
        this.systemVersionSelect = <HTMLSelectElement>document.getElementById("select-test-systemversion");
        this.testSetFormGroupDiv = <HTMLDivElement>document.getElementById("div-testsetformgroup");                    // on test page
        let testSetSelectedDiv = <HTMLDivElement>document.getElementById("div-testset-selected");   // on create page
        this.whitelistingDiv = <HTMLDivElement>document.getElementById("div-whitelisting");
        this.testSetDiv = <HTMLDivElement>document.getElementById("div-testset");

        this.startCreateButton.addEventListener("click", (ev: MouseEvent) => {
            this.switchToCreatePage();
        });

        this.createButton.addEventListener("click", (ev: MouseEvent) => {
            if (testNameInput.value.length === 0) {
                window.alert("Udfyld test navn");
            } else if (testSetSelectedDiv.innerHTML === "&nbsp;") {
                window.alert("Vælg klientsystemejer, -systemnavn og -version");
            } else if (this.selectedGroups.length === 0) {
                window.alert("Vælg en eller flere testgrupper!");
            } else {
                CertTestService.getInstance().createCertTest(testNameInput.value, testSetSelectedDiv.innerText,
                    this.selectedWhitelisting).then((res) => {
                    CertTestAdminApp.certTestPanel.currentTest = res;
                    this.clearGrids();
                    for (let group of res.testGroups) {
                        this.addGridWithData(group.testCases, group.name);
                    }
                    this.testSetDiv.innerHTML = res.testSetName;
                    this.swithToTestPage();
                }).catch(err => CertTestAdminApp.alert("Oprettelse fejlede", err.message));
            }
        });

        this.cloneButton.addEventListener("click", (ev: MouseEvent) => {
            if (testNameInput.value.length === 0) {
                window.alert("Udfyld test navn");
            } else if (testNameInput.value === this.currentTest.name) {
                window.alert("Indtast nyt test navn");
            } else {
                CertTestService.getInstance().cloneCertTest(testNameInput.value, this.currentTest.name).then((res) => {
                    CertTestAdminApp.certTestPanel.currentTest = res;
                    this.clearGrids();
                    for (let group of res.testGroups) {
                        this.addGridWithData(group.testCases, group.name);
                    }
                    this.swithToTestPage();
                }).catch(err => CertTestAdminApp.alert("Kloning fejlede", err.message));
            }
        });

        this.rapportButton.addEventListener("click", (ev: MouseEvent) => {
            CertTestService.getInstance().getTestRapport(this.currentTest.name).then((res) => {
                this.clearGrids();
                for (let group of res.testGroups) {
                    this.addGridWithRapport(group.testCases, group.name);
                }

                let listOfCalls = [];
                for (let group of res.testGroups) {
                    for (let testcase of group.testCases) {
                        for (let rapportElement of testcase.rapport) {
                            listOfCalls.push(rapportElement);
                        }
                    }
                }
                listOfCalls = listOfCalls.filter(function (elem, index, self) {
                    return index === self.indexOf(elem);
                });
                let title = document.createElement("h3");
                title.innerText = "Unikke kald";
                this.testCasesTableDiv.appendChild(title);
                for (let call of listOfCalls) {
                    this.testCasesTableDiv.appendChild(document.createElement("br"));
                    this.testCasesTableDiv.appendChild(document.createTextNode(call));
                }

                this.switchToRapportPage();
            }).catch(err => CertTestAdminApp.alert("Hent fejlede", err.message));
        });

        testNameInput.addEventListener("keyup", (ev: KeyboardEvent) => {
            if (ev.keyCode === 13 || ev.which === 13) {
                // Return key
                this.loadTest(testNameInput.value);
                this.swithToTestPage();
            }
        });

        this.fetchButton.addEventListener("click", (ev: MouseEvent) => {
            if (testNameInput.value.length === 0) {
                window.alert("Udfyld test navn");
            } else {
                this.loadTest(testNameInput.value);
                this.swithToTestPage();
            }
        });

        this.removeButton.addEventListener("click", (ev: MouseEvent) => {
            if (testNameInput.value.length === 0) {
                window.alert("Udfyld test navn");
            } else {
                if (window.confirm("Er du sikker på, at du vil slette testen '" + testNameInput.value + "'?")) {
                    CertTestService.getInstance().removeCertTest(testNameInput.value).then((res) => {
                        CertTestAdminApp.certTestPanel.currentTest = null;
                        this.clearGrids();
                        this.switchToFrontPage();
                        CertTestAdminApp.alert("", "Testen '" + testNameInput.value + "' blev slettet.");
                    }).catch(err => CertTestAdminApp.alert("Slet fejlede", err.message));
                }
            }
        });

        this.homeButton.addEventListener("click", (ev: MouseEvent) => {
            this.clearGrids();
            this.switchToFrontPage();
        });

        this.grids = new Array<Grid<CertTestCaseVO>>();


        let gridConfig2 = new GridConfig<string>();

        gridConfig2.columnConfigs = [
            new ColumnConfig<string>("Vælg", "th-02", (data: string) => data, false),
            new ColumnConfig<string>("Gruppe", "th-01", (data: string) => data, true),
            new ColumnConfig<string>("Vis tests", "th-01", (data: string) => data, false)
        ];

        gridConfig2.getColumn(0).renderer = new UseTestGroupRenderer();
        gridConfig2.getColumn(2).renderer = new TestGroupRenderer();

        this.groupGrid = new Grid(gridConfig2);

        this.groupGrid.setStyleName("table table-bordered");
        this.groupGrid.setCaption("");

        this.groupGrid.setSortedByColumn(0, SortDirection.DESCENDING);

        this.testGroupsTableDiv.appendChild(this.groupGrid.element);

        CertTestService.getInstance().getCertTestGroups().then((res) => {
            this.groupGrid.setData(res.testGroups);
        }).catch(err => CertTestAdminApp.alert("Hent fejlede", err.message));

        SystemInfoService.getInstance().getSystemWhitelistings().then((whitelistings) => {
            this.whitelistings = whitelistings;
            for (let systemOwner of whitelistings.map(wl => wl.systemOwnerName).filter(this.onlyUnique)) {
                let optionElement: HTMLOptionElement = document.createElement("option");
                optionElement.innerText = systemOwner;
                this.systemOwnerSelect.add(optionElement);
            }
            this.systemOwnerSelect.selectedIndex = -1;
        }).catch(err => CertTestAdminApp.alert("Læsning af whitelistede systemer fejlede", err.message));

        this.systemOwnerSelect.addEventListener("change", ev => {
            this.clearOptions(this.systemNameSelect);
            this.clearOptions(this.systemVersionSelect);
            testSetSelectedDiv.innerHTML = "&nbsp;";
            for (let systemName of this.whitelistings
                .filter(wl => wl.systemOwnerName === this.systemOwnerSelect.value)
                .map(wl => wl.systemName)
                .filter(this.onlyUnique)) {
                let optionElement: HTMLOptionElement = document.createElement("option");
                optionElement.innerText = systemName;
                this.systemNameSelect.add(optionElement);
            }
            this.systemNameSelect.selectedIndex = -1;
        });

        this.systemNameSelect.addEventListener("change", ev => {
            this.clearOptions(this.systemVersionSelect);
            testSetSelectedDiv.innerHTML = "&nbsp;";
            for (let systemVersion of this.whitelistings
                .filter(wl => wl.systemOwnerName === this.systemOwnerSelect.value && wl.systemName === this.systemNameSelect.value)
                .map(wl => wl.systemVersion)
                .filter(this.onlyUnique)) {
                let optionElement: HTMLOptionElement = document.createElement("option");
                optionElement.innerText = systemVersion;
                this.systemVersionSelect.add(optionElement);
            }
            this.systemVersionSelect.selectedIndex = -1;
        });


        this.systemVersionSelect.addEventListener("change", ev => {
            this.selectedWhitelisting = this.whitelistings.filter(
                wl => wl.systemOwnerName === this.systemOwnerSelect.value
                    && wl.systemName === this.systemNameSelect.value
                    && wl.systemVersion === this.systemVersionSelect.value
            )[0];

            let FMKVersionString: string = "FMK " + this.selectedWhitelisting.fmkVersion + (this.selectedWhitelisting.eoVersion ? ", EO " + this.selectedWhitelisting.eoVersion : "") + (this.selectedWhitelisting.porVersion ? ", POR " + this.selectedWhitelisting.porVersion : "");
            testSetSelectedDiv.innerText = FMKVersionString;
        }
        );

        this.switchToFrontPage();
    }

    public focusTestCase(testCaseId: string) {
        for (let gridnr = 0; gridnr < this.grids.length; gridnr++) {
            for (let row = 0; row < this.grids[gridnr].getData().length; row++) {
                if (testCaseId === this.grids[gridnr].getData()[row].testID) {
                    let elm = this.grids[gridnr].getElement(row);
                    let td = this.grids[gridnr].getElement(row, 7);
                    if (td) {
                        (<HTMLElement>td.firstElementChild).focus();
                    }
                    break;
                }
            }
        }
    }

    public reload(): Promise<boolean> {
        this.clearGrids();

        const promise = CertTestService.getInstance().getCertTest(CertTestAdminApp.certTestPanel.currentTest.name).then((res) => {
            CertTestAdminApp.certTestPanel.currentTest = res;
            this.clearGrids();
            for (let group of res.testGroups) {
                this.addGridWithData(group.testCases, group.name);
            }
            return true;
        });

        promise.catch(err => CertTestAdminApp.alert("Hent fejlede", err.message));

        return promise;
    }

    private clearOptions(selectbox: HTMLSelectElement) {
        for (let i: number = selectbox.options.length - 1; i >= 0; i--) {
            selectbox.remove(i);
        }
    }

    private onlyUnique<T>(value: T, index: number, array: T[]) {
        return array.indexOf(value) === index;
    }

    private swithToTestPage() {
        this.cloneButton.classList.remove("hidden");
        this.whitelistingDiv.classList.add("hidden");
        this.fetchButton.classList.remove("hidden");
        this.removeButton.classList.remove("hidden");
        this.testSetFormGroupDiv.classList.remove("hidden");
        this.homeButton.classList.remove("hidden");
        this.testGroupsTableDiv.classList.add("hidden");
        this.createButton.classList.add("hidden");
        this.testCasesTableDiv.classList.remove("hidden");
        this.rapportButton.classList.remove("hidden");
    }

    private switchToFrontPage() {
        this.cloneButton.classList.add("hidden");
        this.whitelistingDiv.classList.add("hidden");
        this.fetchButton.classList.remove("hidden");
        this.removeButton.classList.remove("hidden");
        this.testSetFormGroupDiv.classList.add("hidden");
        this.homeButton.classList.add("hidden");
        this.testGroupsTableDiv.classList.add("hidden");
        this.createButton.classList.add("hidden");
        this.startCreateButton.classList.remove("hidden");
        this.testCasesTableDiv.classList.add("hidden");
        this.rapportButton.classList.add("hidden");
    }

    private switchToCreatePage() {
        this.cloneButton.classList.add("hidden");
        this.whitelistingDiv.classList.remove("hidden");
        this.fetchButton.classList.add("hidden");
        this.removeButton.classList.add("hidden");
        this.testSetFormGroupDiv.classList.add("hidden");
        this.homeButton.classList.remove("hidden");
        this.testGroupsTableDiv.classList.remove("hidden");
        this.createButton.classList.remove("hidden");
        this.startCreateButton.classList.add("hidden");
        this.testCasesTableDiv.classList.add("hidden");
        this.rapportButton.classList.add("hidden");
    }

    private switchToRapportPage() {
        this.cloneButton.classList.remove("hidden");
        this.whitelistingDiv.classList.add("hidden");
        this.fetchButton.classList.remove("hidden");
        this.removeButton.classList.remove("hidden");
        this.testSetFormGroupDiv.classList.remove("hidden");
        this.homeButton.classList.remove("hidden");
        this.testGroupsTableDiv.classList.add("hidden");
        this.createButton.classList.add("hidden");
        this.testCasesTableDiv.classList.remove("hidden");
        this.rapportButton.classList.add("hidden");
    }

    private loadTest(name: string) {
        CertTestService.getInstance().getCertTest(name).then((res) => {
            CertTestAdminApp.certTestPanel.currentTest = res;
            if (res === null) {
                this.clearGrids();
            } else {
                this.clearGrids();
                for (let group of res.testGroups) {
                    this.addGridWithData(group.testCases, group.name);
                }
                this.testSetDiv.innerHTML = res.testSetName;
            }
        }).catch(err => CertTestAdminApp.alert("Hent fejlede", err.message));

    }

    private clearGrids() {
        while (this.testCasesTableDiv.firstChild) {
            this.testCasesTableDiv.removeChild(this.testCasesTableDiv.firstChild);
        }
    }

    private addGridWithData(certTestCase: CertTestCaseVO[], name: string) {

        let gridConfig = new GridConfig<CertTestCaseVO>();

        gridConfig.columnConfigs = [
            new ColumnConfig<CertTestCaseVO>("Test", "th-01", (data: CertTestCaseVO) => data.testID, true),
            new ColumnConfig<CertTestCaseVO>("Titel", "th-02", (data: CertTestCaseVO) => data.title, true),
            new ColumnConfig<CertTestCaseVO>("Forventede requests", "th-03", (data: CertTestCaseVO) => data.expectedRequests, true),
            new ColumnConfig<CertTestCaseVO>("Beskrivelse", "th-04", (data: CertTestCaseVO) => data.description, false),
            new ColumnConfig<CertTestCaseVO>("Parametre", "th-05", (data: CertTestCaseVO) => data, true),
            new ColumnConfig<CertTestCaseVO>("Status", "th-06", (data: CertTestCaseVO) => this.statusToString(data.status), true),
            new ColumnConfig<CertTestCaseVO>("Resultat", "th-07", (data: CertTestCaseVO) => data, true),
            new ColumnConfig<CertTestCaseVO>("Start/stop", "th-08", (data: CertTestCaseVO) => data, false),
            new ColumnConfig<CertTestCaseVO>("Ignorér", "th-09", (data: CertTestCaseVO) => data, false),
            new ColumnConfig<CertTestCaseVO>("Startet", "th-10", (data: CertTestCaseVO) => data.startedDatetime ? new Date(data.startedDatetime) : "", false)
        ];

        // Test subject column (CPR)
        gridConfig.getColumn(4).renderer = new TestCaseRenderer((test, testCase) => new SubjectTestCaseWidget(test, testCase));

        // Result column
        gridConfig.getColumn(6).renderer = new TestCaseResultRenderer();

        // Start/Stop column
        gridConfig.getColumn(7).renderer = new TestCaseRenderer((test, testCase) => new StartStopTestCaseWidget(test, testCase));

        // Ignore column
        gridConfig.getColumn(8).renderer = new TestCaseRenderer((test, testCase) => new IgnoreTestCaseWidget(test, testCase));

        let grid = new Grid(gridConfig);
        grid.setStyleName("table table-bordered");
        grid.setCaption(name);
        grid.setSortedByColumn(0, SortDirection.DESCENDING);
        grid.setData(certTestCase);

        this.grids.push(grid);
        this.testCasesTableDiv.appendChild(grid.element);
    }

    private addGridWithRapport(certTestCaseRapport: CertTestCaseRapportVO[], name: string) {
        let gridConfig = new GridConfig<CertTestCaseRapportVO>();

        gridConfig.columnConfigs = [
            new ColumnConfig<CertTestCaseRapportVO>("Test", "th-02", (data: CertTestCaseRapportVO) => data.testID, true),
            new ColumnConfig<CertTestCaseRapportVO>("Titel", "th-01", (data: CertTestCaseRapportVO) => data.title, true),
            new ColumnConfig<CertTestCaseRapportVO>("Kald", "th-01", (data: CertTestCaseRapportVO) => data.rapport, false)
        ];

        gridConfig.getColumn(2).renderer = new MultiLineRenderer();

        let grid = new Grid(gridConfig);
        grid.setStyleName("table table-bordered");
        grid.setCaption(name);
        grid.setSortedByColumn(0, SortDirection.DESCENDING);
        grid.setData(certTestCaseRapport);

        // this.grids.push(grid);
        this.testCasesTableDiv.appendChild(grid.element);
    }

    private statusToString(status: string) {
        switch (status) {
            case TestCaseStatus.READY:
                return "Klar";
            case TestCaseStatus.STARTED:
                return "Startet";
            case TestCaseStatus.EXPIRED:
                return "Udløbet";
            case TestCaseStatus.FINISHED:
                return "Afsluttet";
            default:
                return "Ukendt tilstand: " + status;
        }
    }

    private addVersionOption(select: HTMLSelectElement, testSet: TestSet) {
        let optionElement = document.createElement("option");
        optionElement.innerText = TestSetUtil.toString(testSet);

        select.options.add(optionElement);
    }
}
